import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PALETTE } from "../tokens";
import { useAuth } from "../context/AuthContext";
import { reportApi } from "../api/report";
import { DOMAIN_WORD, DOMAIN_META } from "../utils/domainLabels";
import { ShareReportModal } from "../components/ShareReportModal";

/* ──────────────────────────────────────────────────────────────────
 * Report — the in-app preview of the practitioner report.
 *
 * The share journey's missing heart: the user sees EXACTLY what
 * they're vouching for before any link is minted. Consumes the same
 * JSON endpoint the PDF and public share view render from
 * (GET /report/{user_id}), so preview and shared artifact cannot
 * drift on the numbers.
 *
 * Below the summary threshold (REPORT_GATE check-ins) this screen is
 * the locked state — no share affordance at all, just the honest
 * "unlocks after N more check-ins" line. Matches the backend's own
 * ≥3-snapshot gate on the practitioner summary.
 *
 * Score note: this surface intentionally renders raw rci_total-based
 * numbers (domain_averages * 100), NOT presentedScore — same contract
 * as the shared report itself (see utils/presentedScore.js).
 * ────────────────────────────────────────────────────────────────── */

export const REPORT_GATE = 3;

const STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  .report-screen { font-family: 'DM Sans', sans-serif; background: ${PALETTE.bg}; min-height: 100vh; color: ${PALETTE.text}; max-width: 420px; margin: 0 auto; }
  .report-screen .serif { font-family: 'Playfair Display', Georgia, serif; }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .rp-fu { animation: fadeUp 0.5s ease forwards; opacity: 0; }
  .rp-d1 { animation-delay: 0.05s; }
  .rp-d2 { animation-delay: 0.15s; }
  .rp-d3 { animation-delay: 0.25s; }
  .rp-card { background: #FDFAF6; border: 1px solid ${PALETTE.border}; border-radius: 16px; padding: 20px; margin-bottom: 14px; box-shadow: 0 1px 6px rgba(28,24,20,0.04); }
  .rp-share-btn { transition: opacity 0.15s ease; }
  .rp-share-btn:hover { opacity: 0.9; }
`;

function fmtShortDate(iso) {
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

const TREND_COPY = {
  improving: "Trending up over this window",
  declining: "Trending down over this window",
  stable: "Holding steady over this window",
  insufficient_data: "Not enough days yet to read a trend",
};

function BackArrow() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  );
}

function DomainBar({ code, value }) {
  const pct = Math.round((value || 0) * 100);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0" }}>
      <span style={{ width: 72, fontSize: 12.5, color: PALETTE.textMid, fontWeight: 400 }}>
        {DOMAIN_WORD[code]}
      </span>
      <div style={{ flex: 1, height: 5, borderRadius: 99, background: PALETTE.surfaceAlt, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: PALETTE.primary, borderRadius: 99 }} />
      </div>
      <span className="serif" style={{ width: 34, textAlign: "right", fontSize: 16, fontStyle: "italic", color: PALETTE.text }}>
        {pct}
      </span>
    </div>
  );
}

export default function Report() {
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    reportApi
      .get(user.id, token)
      .then((data) => { if (!cancelled) setReport(data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, token]);

  const checkIns = report?.total_check_ins ?? 0;
  const locked = !loading && !error && checkIns < REPORT_GATE;
  const remaining = Math.max(1, REPORT_GATE - checkIns);
  const averages = report?.domain_averages || {};
  const overall = Math.round((averages.rci || 0) * 100);
  const findings = report?.key_findings || [];
  const journalOptedIn = user?.research_consent === true;

  return (
    <>
      <style>{STYLES}</style>
      <div className="report-screen">
        <div style={{ padding: "max(54px, calc(env(safe-area-inset-top) + 12px)) 24px 130px" }}>

          {/* Header */}
          <div className="rp-fu rp-d1" style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 22 }}>
            <button
              onClick={() => navigate(-1)}
              aria-label="Back"
              style={{
                width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                background: PALETTE.surface, border: `1px solid ${PALETTE.border}`,
                color: PALETTE.text, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <BackArrow />
            </button>
            <div>
              <div style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: PALETTE.primary, fontWeight: 600, marginBottom: 3 }}>
                Your Signal Report
              </div>
              {report && !locked && (
                <div style={{ fontSize: 12.5, color: PALETTE.textMute, fontWeight: 300 }}>
                  {fmtShortDate(report.date_range?.start)} – {fmtShortDate(report.date_range?.end)} · {checkIns} check-ins
                </div>
              )}
            </div>
          </div>

          {loading && (
            <p style={{ fontSize: 13, color: PALETTE.textMute, fontWeight: 300, textAlign: "center", padding: "60px 0" }}>
              Gathering your report…
            </p>
          )}

          {error && (
            <div className="rp-card" style={{ textAlign: "center" }}>
              <p style={{ fontSize: 13.5, color: PALETTE.textMid, fontWeight: 300, lineHeight: 1.6 }}>
                Couldn't load your report. Check your connection and try again.
              </p>
            </div>
          )}

          {/* ── Locked: below the data-sufficiency threshold ── */}
          {locked && (
            <div className="rp-fu rp-d2 rp-card" style={{ textAlign: "center", padding: "34px 24px" }}>
              <div style={{ fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: PALETTE.primary, fontWeight: 600, marginBottom: 12 }}>
                Still forming
              </div>
              <p className="serif" style={{ fontSize: 21, fontStyle: "italic", lineHeight: 1.35, marginBottom: 10 }}>
                Your report unlocks after {remaining} more {remaining === 1 ? "check-in" : "check-ins"}.
              </p>
              <p style={{ fontSize: 13, color: PALETTE.textMid, fontWeight: 300, lineHeight: 1.65, maxWidth: 280, margin: "0 auto 22px" }}>
                A report worth handing to someone needs a few days of signal behind it. Keep checking in — it'll be ready soon.
              </p>
              <button
                onClick={() => navigate("/checkin")}
                style={{
                  background: PALETTE.dark, color: "#F2EDE3", border: "none",
                  borderRadius: 14, padding: "14px 26px", fontSize: 14,
                  fontFamily: "inherit", fontWeight: 500, cursor: "pointer",
                }}
              >
                Check in today
              </button>
            </div>
          )}

          {/* ── Report preview ── */}
          {report && !locked && !error && (
            <>
              <p className="rp-fu rp-d1" style={{ fontSize: 12.5, color: PALETTE.textMute, fontWeight: 300, lineHeight: 1.6, margin: "0 0 16px" }}>
                This is exactly what someone sees when you share — review it first, then decide.
              </p>

              {/* Signal overview */}
              <div className="rp-fu rp-d2 rp-card">
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.textMute, fontWeight: 500 }}>
                    Signal overview
                  </span>
                  <span className="serif" style={{ fontSize: 30, fontStyle: "italic", color: PALETTE.text }}>
                    {overall}
                  </span>
                </div>
                <p style={{ fontSize: 12, color: PALETTE.textMid, fontWeight: 300, marginBottom: 12 }}>
                  {TREND_COPY[report.trend_direction] || TREND_COPY.stable}
                  {typeof report.consistency_rate === "number" && (
                    <> · checked in {Math.round(report.consistency_rate * 100)}% of days</>
                  )}
                </p>
                {DOMAIN_META.map((d) => (
                  <DomainBar key={d.code} code={d.code} value={averages[d.key]} />
                ))}
              </div>

              {/* What matters this week */}
              {findings.length > 0 && (
                <div className="rp-fu rp-d2 rp-card">
                  <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.textMute, fontWeight: 500, marginBottom: 12 }}>
                    What matters this week
                  </div>
                  {findings.map((line, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, padding: "7px 0" }}>
                      <span style={{ width: 5, height: 5, borderRadius: "50%", background: PALETTE.primary, flexShrink: 0, marginTop: 7 }} />
                      <p style={{ fontSize: 13, color: PALETTE.text, fontWeight: 300, lineHeight: 1.6 }}>{line}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Summary */}
              {report.practitioner_summary && (
                <div className="rp-fu rp-d3 rp-card" style={{ background: PALETTE.surface }}>
                  <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: PALETTE.primary, fontWeight: 600, marginBottom: 10 }}>
                    Your 30-day summary
                  </div>
                  <p className="serif" style={{ fontSize: 14.5, fontStyle: "italic", color: PALETTE.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                    {report.practitioner_summary}
                  </p>
                </div>
              )}

              {/* Journal excerpts disclosure */}
              <div className="rp-fu rp-d3" style={{ padding: "2px 6px 0" }}>
                <p style={{ fontSize: 11.5, color: PALETTE.textMute, fontWeight: 300, lineHeight: 1.6 }}>
                  {journalOptedIn
                    ? "Journal excerpts can be included — you choose for each share, before the link exists."
                    : "Journal excerpts are never included in your shared report."}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Sticky share bar — only when there is a shareable report */}
        {report && !locked && !error && (
          <div style={{
            position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
            width: "100%", maxWidth: 420, padding: "14px 24px 30px",
            background: `linear-gradient(to top, ${PALETTE.bg} 65%, transparent)`,
          }}>
            <button
              className="rp-share-btn"
              onClick={() => setShareOpen(true)}
              style={{
                width: "100%", height: 54,
                background: PALETTE.dark, color: "#F2EDE3",
                border: "none", borderRadius: 16,
                fontSize: 15, fontWeight: 500, letterSpacing: "0.02em",
                fontFamily: "inherit", cursor: "pointer",
                boxShadow: "0 6px 22px rgba(28,24,20,0.18)",
              }}
            >
              Share this report
            </button>
          </div>
        )}

        <ShareReportModal open={shareOpen} onClose={() => setShareOpen(false)} />
      </div>
    </>
  );
}
